import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Plane, PlaneTakeoff, ShieldAlert, Clock, RefreshCcw, Stamp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Bornwood International — /cef/travel/:serverId — the departure board.
   Split-flap board of the whole network: fares scale with what you carry, a
   30-minute cooldown keeps the route honest, and an active warrant means Border
   Control meets you at the gate. A departure queues the bridge handoff op. */

const DEMO_NET = {
  destinations: [
    { id: "SRV-EAST", name: "Bornwood East", region: "US-E", fare: 250, route: "shuttle" },
    { id: "SRV-HARBOR", name: "Harborlight RP", region: "EU", fare: 400, route: "regional" },
    { id: "SRV-PALEROW", name: "Pale Row Stories", region: "AU", fare: 550, route: "long-haul" },
  ],
  cooldown_min: 30, bank_pct: 0.03,
};
const DEMO_LEGS = [
  { id: "l1", from_server: "SRV-EAST", to_server: "SRV-OFFICIAL-1", fare: 310, inspected: false, at: new Date(Date.now() - 86400000 * 3).toISOString() },
];

const STATUS_STYLE = {
  BOARDING: "text-emerald-400", "ON TIME": "text-zinc-400", DETAINED: "text-red-400",
};

export default function TravelCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [net, setNet] = useState(null);
  const [legs, setLegs] = useState([]);
  const [busy, setBusy] = useState("");
  const [detained, setDetained] = useState("");
  const [tick, setTick] = useState(0); // flip-board clock shimmer

  const load = useCallback(async () => {
    if (demo) { setNet(DEMO_NET); setLegs(DEMO_LEGS); return; }
    try {
      const [n, l] = await Promise.all([
        api.get(`/nc/servers/${serverId}/travel/network`, { params: { token } }),
        api.get(`/nc/servers/${serverId}/travel/ledger`, { params: { token } }),
      ]);
      setNet(n.data);
      setLegs(l.data.legs);
    } catch (e) { toast.error(e.response?.data?.detail || "Departures unavailable"); }
  }, [demo, serverId, token]);
  useEffect(() => { load(); const t = setInterval(() => setTick((x) => x + 1), 3000); return () => clearInterval(t); }, [load]);

  const depart = async (d) => {
    setBusy(d.id);
    setDetained("");
    try {
      if (demo) { toast.success(`Booked: ${d.name} — $${d.fare} (demo)`); return; }
      const r = await api.post(`/nc/servers/${serverId}/travel/depart`, { token, to_server: d.id });
      toast.success(r.data.message);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || "Booking failed";
      if (e.response?.status === 403) setDetained(detail);
      toast.error(detail);
    } finally { setBusy(""); }
  };

  const flap = `flap${tick % 2}`;

  return (
    <div data-testid="travel-page" className="min-h-screen bg-transparent flex items-center justify-center p-4 relative select-none">
      {demo && <PreviewRibbon what="departures" />}
      <style>{`
        @keyframes ncFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        @keyframes flapFlicker { 0%,100% { opacity: 1 } 50% { opacity: 0.72 } }
        .flap1 { animation: flapFlicker 3s infinite; }
      `}</style>
      <div className="w-full max-w-2xl border border-white/12 bg-[#05070b]/95 backdrop-blur-md shadow-2xl" style={{ animation: "ncFadeUp 0.3s ease both" }}>
        {/* board header */}
        <header className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <PlaneTakeoff className="w-5 h-5 text-sky-400" />
          <div>
            <h1 className="font-sans font-bold uppercase tracking-[0.3em] text-sm text-zinc-100">Bornwood International</h1>
            <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.25em]">departures · network transfer desk</p>
          </div>
          <button data-testid="travel-refresh" onClick={load} className="ml-auto text-zinc-500 hover:text-zinc-200 transition-colors"><RefreshCcw className="w-3.5 h-3.5" /></button>
        </header>

        {detained && (
          <div data-testid="travel-detained" className="mx-5 mt-4 border border-red-500/50 bg-red-950/30 px-4 py-3 flex items-center gap-3">
            <ShieldAlert className="w-4 h-4 text-red-400 shrink-0 animate-pulse" />
            <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-red-300">{detained}</p>
          </div>
        )}

        {/* split-flap board */}
        <div className="p-5 flex flex-col gap-1.5">
          <div className="grid grid-cols-[1fr_90px_70px_96px] gap-2 px-3 pb-1 font-mono text-[7px] uppercase tracking-[0.3em] text-zinc-600">
            <span>Destination</span><span>Route</span><span>Fare</span><span className="text-right">Gate</span>
          </div>
          {net?.destinations.map((d) => (
            <div key={d.id} data-testid={`dest-${d.id}`}
              className="grid grid-cols-[1fr_90px_70px_96px] gap-2 items-center border border-white/[0.07] bg-black/50 px-3 py-3 hover:border-sky-400/30 transition-colors">
              <div className={`flap1 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-amber-100/90`} key={flap + d.id}>
                {d.name}
                <span className="block font-mono text-[7px] font-normal text-zinc-600 tracking-[0.2em]">{d.region} · {d.id}</span>
              </div>
              <span className="font-mono text-[8px] uppercase tracking-[0.15em] text-zinc-400">{d.route}</span>
              <span className="font-mono text-[10px] font-bold text-emerald-300">${d.fare}</span>
              <button data-testid={`depart-${d.id}`} onClick={() => depart(d)} disabled={!!busy}
                className="justify-self-end font-mono text-[8px] font-bold uppercase tracking-[0.2em] bg-sky-400 hover:bg-sky-300 disabled:opacity-40 text-black px-3 py-2 transition-colors flex items-center gap-1">
                <Plane className="w-3 h-3" /> {busy === d.id ? "…" : "Board"}
              </button>
            </div>
          ))}
          <p className="font-mono text-[7px] text-zinc-600 uppercase tracking-[0.2em] mt-2 flex items-center gap-1.5 px-1">
            <Clock className="w-2.5 h-2.5" /> fare = base + {Math.round((net?.bank_pct ?? 0.03) * 100)}% of carried bank · one departure per {net?.cooldown_min ?? 30} min · active warrants are detained at the gate
          </p>
        </div>

        {/* passport ledger */}
        <div className="border-t border-white/10 px-5 py-4">
          <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-1.5 mb-2">
            <Stamp className="w-3 h-3" /> Passport stamps
          </p>
          {legs.length === 0 && <p className="font-mono text-[9px] text-zinc-600">Clean passport. Go see the network.</p>}
          <div className="flex flex-wrap gap-1.5">
            {legs.map((l) => (
              <span key={l.id} data-testid={`leg-${l.id}`}
                className="font-mono text-[8px] uppercase tracking-[0.12em] border border-white/10 bg-black/40 px-2.5 py-1.5 text-zinc-400">
                {l.from_server} → {l.to_server} · ${l.fare}{l.inspected ? " · inspected" : ""}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
